import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { API_CONSTANTS, NIGHTWEB_APP_CONSTANT } from '../constants';
import {
  GeoPoint,
  MOCK_FALLBACK_CENTER,
  applyGpsJitter,
  buildMockTrailSampleTimes,
  destinationPoint,
  haversineDistanceMeters,
  toLocationRow,
} from '../utils';
import { ThreatPairZoneRadiiMeters } from '../services';

/** How far inside the Red radius / outside the Yellow radius the mock orbit swings. */
const ORBIT_INNER_FACTOR = 0.3;
const ORBIT_OUTER_FACTOR = 1.4;

function orbitPosition(
  center: GeoPoint,
  zoneRadiiMeters: ThreatPairZoneRadiiMeters,
  atMs: number,
): GeoPoint {
  const periodMs = NIGHTWEB_APP_CONSTANT.axisLinkMockPerpetratorOrbitPeriodMs;
  const phase = (((atMs % periodMs) + periodMs) % periodMs) / periodMs; // 0..1, always positive
  const minRadius = zoneRadiiMeters.red * ORBIT_INNER_FACTOR;
  const maxRadius = zoneRadiiMeters.yellow * ORBIT_OUTER_FACTOR;
  // A second, faster/smaller wobble layered on the base cosine swing breaks up the otherwise
  // perfectly smooth spiral, closer to how a real, imprecisely-sampled track looks.
  const wobble = 1 + 0.08 * Math.sin(2 * Math.PI * phase * 5.3);
  const distance =
    (minRadius + (maxRadius - minRadius) * (0.5 - 0.5 * Math.cos(2 * Math.PI * phase))) * wobble;
  const bearing = (phase * 720 + 6 * Math.sin(2 * Math.PI * phase * 3.7)) % 360;

  return applyGpsJitter(destinationPoint(center, distance, bearing), atMs, 0);
}

function buildMockPerpetratorLocationsResponse(
  referencePosition: GeoPoint | null,
  zoneRadiiMeters: ThreatPairZoneRadiiMeters,
) {
  const center = referencePosition ?? MOCK_FALLBACK_CENTER;
  const now = Date.now();
  const sampleTimes = buildMockTrailSampleTimes(now);

  const rows = sampleTimes.map((atMs, index) => {
    const point = orbitPosition(center, zoneRadiiMeters, atMs);
    const isLatest = index === sampleTimes.length - 1;
    const emergency =
      isLatest && haversineDistanceMeters(center, point) <= zoneRadiiMeters.red;
    return toLocationRow(point, atMs, emergency);
  });

  return { data: { count: rows.length, rows } };
}

/**
 * Mock (NEX-1534): stands in for the Monitored Subject (perpetrator) GPS endpoint, which doesn't
 * exist on the backend yet. `ThreatPairService.getPerpetratorLocations` makes a genuine HTTP GET
 * to `/end-users/{id}/perpetrator-locations`; this interceptor catches requests to that specific
 * path and returns a synthesized `last-locations`-shaped response instead of letting them reach
 * the server (which has no matching route and would 404).
 *
 * Deliberately built as a real interceptor rather than a service-level `of(...)` short-circuit: it
 * keeps the calling code identical to what it will look like once a real endpoint exists, so
 * wiring that up later means deleting this file and its registration in `app.module.ts` — no
 * change to `ThreatPairService` or anything downstream of it.
 */
@Injectable()
export class PerpetratorLocationsMockInterceptor implements HttpInterceptor {
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (
      request.method !== 'GET' ||
      !request.url.includes(API_CONSTANTS.PERPETRATOR_LOCATIONS)
    ) {
      return next.handle(request);
    }

    const refLat = request.params.get('refLat');
    const refLng = request.params.get('refLng');
    const referencePosition: GeoPoint | null =
      refLat != null && refLng != null
        ? { lat: parseFloat(refLat), lng: parseFloat(refLng) }
        : null;
    const zoneRadiiMeters: ThreatPairZoneRadiiMeters = {
      yellow: parseFloat(request.params.get('yellowRadius') ?? '0'),
      red: parseFloat(request.params.get('redRadius') ?? '0'),
    };

    const body = buildMockPerpetratorLocationsResponse(
      referencePosition,
      zoneRadiiMeters,
    );

    return of(new HttpResponse({ status: 200, body })).pipe(delay(400));
  }
}
